import { PureComponent } from 'react';
import { SavedSearch } from '@sbt-web/network/types';
import type { FiltersConfigState } from '@/reducers/filtersConfig';
import type { ItemsState } from '@/reducers/items';
import type { SearchState } from '@/reducers/search';
import type { SaveSearchProvider } from '@/shared/helpers/SavedSearches';
import {
  buildIndex,
  SavedSearchesIndex,
} from '@/shared/helpers/SavedSearches/matcher';
import { DefaultTrackingEvents } from '@/shared/helpers/SavedSearches/trackingEvents';
import { getOrCreatePulse } from '@/tools/tracking/utils';
import SnackBarContent from './SnackBarContent';
import {
  createDisplayStrategies as defaultCreateDisplayStrategies,
  DisplayStrategy,
} from './strategies';

interface SnackBarProps {
  search: SearchState;
  items: ItemsState;
  savedSearchesProvider: SaveSearchProvider;
  filtersConfig: FiltersConfigState;
}

interface SnackBarState {
  show: boolean;
  savedSearches: SavedSearch[];
  savedSearchesIndex: SavedSearchesIndex;
  firstFetchStarted: boolean;
  firstFetchDone: boolean;
  savedSearchButtonOutsideViewport: boolean;
  closed: boolean;
}

class SnackBar extends PureComponent<SnackBarProps, SnackBarState> {
  static displayName = 'SnackBar';

  state: SnackBarState;

  private displayStrategies: DisplayStrategy;

  constructor(props: SnackBarProps) {
    super(props);

    this.state = {
      firstFetchStarted: false,
      firstFetchDone: false,
      show: false,
      savedSearches: [],
      savedSearchButtonOutsideViewport: false,
      closed: false,
      savedSearchesIndex: {},
    };

    this.displayStrategies = defaultCreateDisplayStrategies(
      this.state,
      this.setState.bind(this)
    );
  }

  onClose = () => {
    this.setState(
      {
        closed: true,
      },
      () => {
        getOrCreatePulse()?.queueEvent({
          type: 'Engagement',
          object: {
            id: 'close-search-banner',
            type: 'UIElement',
            elementType: 'banner',
          },
          target: {
            id: '0000000000000000',
            type: 'Listing',
          },
          search: {
            id: '0000000000000000',
            type: 'SearchMetadata',
          },
        });
      }
    );
  };

  onNotifyMe = async () => {
    const { search, savedSearchesProvider, filtersConfig } = this.props;

    await savedSearchesProvider.saveSearch(search, filtersConfig);

    const savedSearches = await savedSearchesProvider.getSavedSearches();

    this.setState({
      savedSearches,
      savedSearchesIndex: buildIndex(savedSearches),
    });
  };

  async componentDidMount() {
    this.displayStrategies = defaultCreateDisplayStrategies(
      this.state,
      this.setState.bind(this)
    );

    window.addEventListener('scroll', this.checkSnackBarDisplayability, {
      passive: true,
    });

    await this.initSavedSearches();
  }

  initSavedSearches = async () => {
    const { savedSearchesProvider } = this.props;

    if (this.state.firstFetchStarted) {
      return this.state.savedSearches || [];
    }

    this.setState({ firstFetchStarted: true });

    const savedSearches = await savedSearchesProvider.getSavedSearches();

    const idx = buildIndex(savedSearches);

    this.setState({
      firstFetchDone: true,
      savedSearches,
      savedSearchesIndex: idx,
    });

    return savedSearches;
  };

  async componentDidUpdate() {
    this.displayStrategies = defaultCreateDisplayStrategies(
      this.state,
      this.setState.bind(this)
    );

    if (this.state.firstFetchStarted) {
      await this.checkSnackBarDisplayability();
    } else {
      await this.initSavedSearches();
      await this.checkSnackBarDisplayability();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.checkSnackBarDisplayability);
  }

  /**
   *  This method checks against the strategies if the snack bar should be displayed.
   *  Every strategy should evaluate to true in order to be able to display the snackbar
   */
  checkSnackBarDisplayability = async () => {
    const { search, filtersConfig } = this.props;

    return await Promise.all(
      Object.values(this.displayStrategies).map((ds) =>
        ds(search, filtersConfig)
      )
    )
      .then((evaluations) => {
        const show = evaluations.every((result) => result);
        const prevShowState = this.state.show;
        this.setState({ show });
        return [show, prevShowState];
      })
      .then(([shouldDisplay, prevShowState]) => {
        if (shouldDisplay && shouldDisplay !== prevShowState) {
          getOrCreatePulse()?.queueEvent({
            type: 'View',
            object: {
              id: 'save-search-banner',
              type: 'UIElement',
              elementType: 'banner',
            },
            target: {
              id: '0000000000000000',
              type: 'Listing',
            },
            search: {
              id: '0000000000000000',
              type: 'SearchMetadata',
            },
          });
        }
      });
  };

  get shouldSnackBarBeDisplayed() {
    return this.props.items.total > 0 && this.state.show;
  }

  render() {
    return (
      <SnackBarContent
        show={this.shouldSnackBarBeDisplayed}
        onClose={this.onClose}
        onNotifyMe={this.onNotifyMe}
      />
    );
  }
}

class SnackBarTrackingEvents extends DefaultTrackingEvents {
  onBeforeSave() {
    getOrCreatePulse()?.queueEvent({
      type: 'Engagement',
      object: {
        id: 'save-search-banner-avvisami',
        type: 'UIElement',
        elementType: 'banner',
      },
      target: {
        id: '0000000000000000',
        type: 'Listing',
      },
      search: {
        '@id': '0000000000000000',
        '@type': 'SearchMetadata',
      },
    });
  }
}

export default SnackBar;
export { SnackBarTrackingEvents };
export type { SnackBarProps, SnackBarState };
